import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Redirect, Link, useHistory, useLocation } from "react-router-dom";
import {
  FaClipboardList,
  FaPlus,
  FaArrowLeft,
  FaUniversity,
  FaLanguage,
  FaEdit,
  FaUsers,
  FaCheckCircle,
  FaCog,
  FaCheckDouble,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import COLORS from "../resources/colors/COLORS";
import SessionManager from "../Utils/SessionManager";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";
import myToast from "../Utils/MyToast";
import MdlSelectSession from "../dialog/MdlSelectSession";
import BackButton from "../ChildComponent/BackButton";

const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;

const EXAM_TYPES = [
  { value: "EXAM", label: "1 - EXAM" },
  { value: "CLASS_TEST", label: "2 - CLASS_TEST" },
];

function normalizeExamType(value) {
  const v = String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "_");
  return v === "CLASS_TEST" || v === "CLASSTEST" ? "CLASS_TEST" : "EXAM";
}

const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

function getItemKey(item) {
  return `${item.class_id}-${item.section_id}`;
}

function getClassLabel(item) {
  if (!item) return "";
  return item.section_id === "No"
    ? item.class_name || ""
    : `${item.class_name || ""}:${item.section_name || ""}`;
}

function getSubjectNames(item) {
  const list = Array.isArray(item?.subject) ? item.subject : [];
  const names = [];
  for (let i = 0; i < list.length; i += 1) {
    const name = list[i]?.subject_name || list[i]?.exam_paper_name;
    if (name) names.push(name);
  }
  return names;
}

function buildSubjectIdMap(item) {
  const map = Object.create(null);
  const list = Array.isArray(item?.subject) ? item.subject : [];
  for (let i = 0; i < list.length; i += 1) {
    const sj = list[i];
    const name = sj?.subject_name || sj?.exam_paper_name;
    if (name && sj?.subject_id) map[name] = String(sj.subject_id);
  }
  return map;
}

function buildFilteredClassList(classData, boardId, mediumId) {
  if (!boardId || !mediumId || !Array.isArray(classData) || classData.length === 0) {
    return [];
  }

  const result = [];
  for (let i = 0; i < classData.length; i += 1) {
    const item = classData[i];
    if (item.board_id !== boardId || item.medium_id !== mediumId) continue;

    if (item.section === "No") {
      result.push({
        ...item,
        section_id: "No",
        subject: Array.isArray(item.subject) ? item.subject : [],
      });
      continue;
    }

    const sections = Array.isArray(item.section) ? item.section : [];
    for (let j = 0; j < sections.length; j += 1) {
      const section = sections[j];
      result.push({
        ...section,
        board_id: boardId,
        board_name: item.board_name,
        medium_id: mediumId,
        medium_name: item.medium_name,
        class_id: section.class_id || item.class_id,
        class_name: section.class_name || item.class_name,
        subject: Array.isArray(section.subject)
          ? section.subject
          : Array.isArray(item.subject)
            ? item.subject
            : [],
      });
    }
  }

  result.sort((a, b) => {
    const aId = parseInt(a.class_id, 10);
    const bId = parseInt(b.class_id, 10);
    if (!Number.isNaN(aId) && !Number.isNaN(bId)) return aId - bId;
    return String(a.class_id).localeCompare(String(b.class_id));
  });

  return result;
}

function Field({ label, required, children, hint, className = "" }) {
  return (
    <div className={`sr-field${className ? ` ${className}` : ""}`}>
      <label className="sr-field__label">
        {label}
        {required ? <span className="sr-field__req">*</span> : null}
      </label>
      {children}
      {hint ? <span className="sr-field__hint">{hint}</span> : null}
    </div>
  );
}

function SectionCard({
  icon: Icon,
  title,
  subtitle,
  children,
  action,
  className = "",
}) {
  return (
    <section className={`sr-section${className ? ` ${className}` : ""}`}>
      <div className="sr-section__head">
        <span className="sr-section__icon" aria-hidden>
          <Icon />
        </span>
        <div className="min-w-0">
          <h2 className="sr-section__title">{title}</h2>
          {subtitle ? <p className="sr-section__sub">{subtitle}</p> : null}
        </div>
        {action ? <div className="sr-section__action">{action}</div> : null}
      </div>
      <div className="sr-section__body">{children}</div>
    </section>
  );
}

const ClassCard = memo(function ClassCard({
  item,
  isActive,
  selectedCount,
  subjects,
  selectedSubjectSet,
  onToggleClass,
  onToggleSubject,
  onToggleAllSubjects,
  onManageSubject,
}) {
  const key = getItemKey(item);
  const classLabel = getClassLabel(item);
  const allSelected =
    subjects.length > 0 && selectedCount === subjects.length;

  return (
    <article className={`ec-class-card${isActive ? " is-active" : ""}`}>
      <div className="ec-class-card__head">
        <button
          type="button"
          className={`ec-class-card__title${isActive ? " is-active" : ""}`}
          onClick={() => onToggleClass(item)}
          aria-pressed={isActive}
          title={isActive ? "Click to deselect class" : "Click to select class"}
        >
          <span
            className={`ec-class-card__check${isActive ? " is-checked" : ""}`}
            aria-hidden
          >
            {isActive ? "✓" : ""}
          </span>
          <span className="ec-class-card__name">{classLabel}</span>
        </button>
        <div className="ec-class-card__tools">
          <span className="ec-selected-badge">{selectedCount} selected</span>
          <button
            type="button"
            className={`ec-action-btn${allSelected ? " is-active" : ""}`}
            onClick={() => onToggleAllSubjects(item)}
          >
            <FaCheckDouble aria-hidden />
            {allSelected ? "Deselect All Subject" : "Select All Subject"}
          </button>
          <button
            type="button"
            className="ec-action-btn"
            onClick={onManageSubject}
          >
            <FaLanguage aria-hidden /> Manage Subject
          </button>
        </div>
      </div>
      <div className="ec-subject-grid">
        {subjects.length === 0 ? (
          <span className="ec-subject-empty">No subjects available</span>
        ) : (
          subjects.map((subject) => {
            const isChecked = !!(
              selectedSubjectSet && selectedSubjectSet.has(subject)
            );
            return (
              <label
                key={`${key}-${subject}`}
                className={`ec-subject-chip${isChecked ? " is-active" : ""}`}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={() => onToggleSubject(item, subject)}
                />
                <span>{subject}</span>
              </label>
            );
          })
        )}
      </div>
    </article>
  );
});

const ExamCreation = () => {
  const loggedIn = localStorage.getItem("token") !== null;
  const history = useHistory();
  const location = useLocation();
  const initialExamType = normalizeExamType(
    new URLSearchParams(location.search).get("type")
  );
  const listBackPath =
    initialExamType === "CLASS_TEST"
      ? "/AdminMain/ClassTestList"
      : "/AdminMain/ExamList";
  const listBackTo = {
    pathname: listBackPath,
    state: { refreshExamList: Date.now() },
  };
  const listBackLabel =
    initialExamType === "CLASS_TEST" ? "Class Test List" : "Exam List";

  const [loading, setLoading] = useState(false);
  const [mClassData, setClass] = useState([]);
  const [classBoard, setClassBoard] = useState([]);
  const [classMedium, setClassMedium] = useState([]);
  const [selectedBoard, setSelectedBoard] = useState("");
  const [selectedMedium, setSelectedMedium] = useState("");
  const [selectedClasses, setSelectedClasses] = useState({});
  const [selectedSubjectsByClass, setSelectedSubjectsByClass] = useState({});
  const [examName, setExamName] = useState("");
  const [examType, setExamType] = useState(initialExamType);
  const [remark, setRemark] = useState("");
  const [remarkTouched, setRemarkTouched] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [isSuccessOpen, setIsSuccessOpen] = useState(false);
  const [createdExam, setCreatedExam] = useState(null);
  const [isOpenSessionDialog, setIsOpenSessionDialog] = useState(false);
  const [SESSION_ID, setSESSION_ID] = useState("");
  const [SESSION_NAME, setSESSION_NAME] = useState("");
  const [SESSION_STATUS, setSESSION_STATUS] = useState("");

  const filteredClassSectionList = useMemo(
    () => buildFilteredClassList(mClassData, selectedBoard, selectedMedium),
    [mClassData, selectedBoard, selectedMedium]
  );

  const subjectsByKey = useMemo(() => {
    const map = Object.create(null);
    for (let i = 0; i < filteredClassSectionList.length; i += 1) {
      const item = filteredClassSectionList[i];
      map[getItemKey(item)] = getSubjectNames(item);
    }
    return map;
  }, [filteredClassSectionList]);

  const selectedSubjectTotal = useMemo(() => {
    let sum = 0;
    const values = Object.values(selectedSubjectsByClass);
    for (let i = 0; i < values.length; i += 1) {
      sum += values[i]?.size || 0;
    }
    return sum;
  }, [selectedSubjectsByClass]);

  const selectedClassTotal = useMemo(() => {
    let count = 0;
    const values = Object.values(selectedClasses);
    for (let i = 0; i < values.length; i += 1) {
      if (values[i]) count += 1;
    }
    return count;
  }, [selectedClasses]);

  const fetchClassData = useCallback(async (sessionId) => {
    setLoading(true);
    try {
      const { data: response } = await axios.get(
        `${API_BASE()}getClassWithSubjects.php`,
        {
          params: {
            school_code: SessionManager.getSchoolCode(),
            session_id: sessionId,
          },
        }
      );

      if (response?.api_status === "OK") {
        const boards = response.data?.board || [];
        const mediums = response.data?.medium || [];
        setClass(response.data?.class || []);
        setClassBoard(boards);
        setClassMedium(mediums);
        setSelectedBoard(boards[0]?.board_id || "");
        setSelectedMedium(mediums[0]?.medium_id || "");
        setSelectedClasses({});
        setSelectedSubjectsByClass({});
      } else {
        myToast.notify_warning(response?.api_remark || "Could not load classes.");
      }
    } catch (error) {
      console.error("API Error>>>>", error);
      myToast.notify_warning("Network error loading classes.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const mSession = SessionManager.getCurrentSession();
    if (mSession?.[0]) {
      setSESSION_ID(mSession[0].session_id);
      setSESSION_NAME(mSession[0].session_name);
      setSESSION_STATUS(mSession[0].status);
      fetchClassData(mSession[0].session_id);
    } else {
      fetchClassData("");
    }
  }, [fetchClassData]);

  // Keep selection maps aligned with current filtered classes (prune stale keys).
  useEffect(() => {
    const validKeys = new Set(
      filteredClassSectionList.map((item) => getItemKey(item))
    );

    setSelectedClasses((prev) => {
      const next = {};
      let changed = false;
      validKeys.forEach((key) => {
        next[key] = !!prev[key];
        if (prev[key] === undefined) changed = true;
      });
      for (const key of Object.keys(prev)) {
        if (!validKeys.has(key)) {
          changed = true;
          break;
        }
      }
      return changed ? next : prev;
    });

    setSelectedSubjectsByClass((prev) => {
      const next = {};
      let changed = false;
      validKeys.forEach((key) => {
        next[key] = prev[key] instanceof Set ? prev[key] : new Set();
        if (!(prev[key] instanceof Set)) changed = true;
      });
      for (const key of Object.keys(prev)) {
        if (!validKeys.has(key)) {
          changed = true;
          break;
        }
      }
      return changed ? next : prev;
    });
  }, [filteredClassSectionList]);

  useEffect(() => {
    if (remarkTouched) return;
    const boardName =
      classBoard.find((b) => b.board_id === selectedBoard)?.board_name || "";
    const mediumName =
      classMedium.find((m) => m.medium_id === selectedMedium)?.medium_name ||
      "";
    const autoRemark = [boardName, mediumName].filter(Boolean).join(" - ");
    if (autoRemark) setRemark(autoRemark);
  }, [selectedBoard, selectedMedium, classBoard, classMedium, remarkTouched]);

  const toggleClassSelected = useCallback((item) => {
    const key = getItemKey(item);
    setSelectedClasses((prevClasses) => {
      const selecting = !prevClasses[key];
      setSelectedSubjectsByClass((prevSub) => ({
        ...prevSub,
        [key]: selecting
          ? prevSub[key] instanceof Set
            ? prevSub[key]
            : new Set()
          : new Set(),
      }));
      return { ...prevClasses, [key]: selecting };
    });
  }, []);

  const toggleSubjectSelected = useCallback((item, subject) => {
    const key = getItemKey(item);
    setSelectedSubjectsByClass((prev) => {
      const nextSet = new Set(prev[key] || []);
      if (nextSet.has(subject)) nextSet.delete(subject);
      else nextSet.add(subject);
      if (nextSet.size > 0) {
        setSelectedClasses((classes) =>
          classes[key] ? classes : { ...classes, [key]: true }
        );
      }
      return { ...prev, [key]: nextSet };
    });
  }, []);

  const toggleAllSubjects = useCallback(
    (item) => {
      const key = getItemKey(item);
      const allSubjects = subjectsByKey[key] || getSubjectNames(item);
      setSelectedSubjectsByClass((prev) => {
        const current = prev[key] || new Set();
        const allSelected =
          current.size === allSubjects.length && allSubjects.length > 0;
        const nextSet = allSelected ? new Set() : new Set(allSubjects);
        if (!allSelected) {
          setSelectedClasses((classes) =>
            classes[key] ? classes : { ...classes, [key]: true }
          );
        }
        return { ...prev, [key]: nextSet };
      });
    },
    [subjectsByKey]
  );

  const openManageSubject = useCallback(() => {
    history.push("/AdminMain/ClassSubject");
  }, [history]);

  const handleSubmitExamCreation = useCallback(async () => {
    const name = examName.trim();
    if (!name) {
      myToast.notify_warning("Please enter an exam name");
      return;
    }
    if (!examType) {
      myToast.notify_warning("Please select exam type");
      return;
    }

    const classesWithoutSubjects = [];
    const classSelections = [];

    for (let i = 0; i < filteredClassSectionList.length; i += 1) {
      const item = filteredClassSectionList[i];
      const key = getItemKey(item);
      const subjectsSet = selectedSubjectsByClass[key];
      const subjectCount = subjectsSet?.size || 0;

      if (selectedClasses[key] && subjectCount === 0) {
        classesWithoutSubjects.push(getClassLabel(item) || key);
        continue;
      }

      if (subjectCount === 0) continue;

      const idMap = buildSubjectIdMap(item);
      const selectedSubjectObjs = [];
      subjectsSet.forEach((subjectName) => {
        const subject_id = idMap[subjectName];
        if (subject_id) {
          selectedSubjectObjs.push({ subject_id, isChecked: "true" });
        }
      });

      if (selectedSubjectObjs.length === 0) continue;

      classSelections.push({
        class_id: item.class_id,
        section_id: item.section_id,
        isChecked: "true",
        subject: JSON.stringify(selectedSubjectObjs),
      });
    }

    if (classesWithoutSubjects.length > 0) {
      myToast.notify_warning(
        `Select at least one subject for: ${classesWithoutSubjects.join(", ")}`
      );
      return;
    }

    if (classSelections.length === 0) {
      myToast.notify_warning("Select at least one class with a subject");
      return;
    }

    setSubmitLoading(true);
    try {
      const staffDetails = SessionManager.getStaffDetails() || {};
      const formBody = new URLSearchParams();
      formBody.append("staff_id", staffDetails.staff_id ? String(staffDetails.staff_id) : "");
      formBody.append("session_id", String(SESSION_ID || ""));
      formBody.append("exam_name", name);
      formBody.append("exam_type", String(examType || "EXAM"));
      formBody.append("exam_date", "");
      formBody.append("exam_remark", remark.trim());
      formBody.append("participating_class", JSON.stringify(classSelections));
      formBody.append("school_code", String(SessionManager.getSchoolCode() || ""));

      const { data: response } = await axios.post(
        `${API_BASE()}createExam2.php`,
        formBody.toString(),
        { headers: { "Content-Type": "application/x-www-form-urlencoded" } }
      );

      if (response?.api_status === "OK") {
        myToast.notify_success(
          response.api_remark || "Exam created successfully"
        );
        const newExamId =
          response.exam_id || response.data?.exam_id || null;
        const exam_status =
          response.status_name || response.data?.status_name || "Created";
        setCreatedExam(
          newExamId
            ? {
                exam_id: String(newExamId),
                session_id: String(SESSION_ID || ""),
                exam_name: name,
                exam_status: String(exam_status),
              }
            : null
        );
        setIsSuccessOpen(true);
      } else {
        myToast.notify_warning(response?.api_remark || "Failed to create exam");
      }
    } catch (err) {
      console.error("Submit Exam Creation Error", err);
      myToast.notify_error("Unable to create exam. Please try again.");
    } finally {
      setSubmitLoading(false);
    }
  }, [
    examName,
    examType,
    filteredClassSectionList,
    selectedClasses,
    selectedSubjectsByClass,
    SESSION_ID,
    remark,
  ]);

  const handleSelectedSession = useCallback(
    (session_id, session_name, session_status) => {
      setSESSION_ID(session_id);
      setSESSION_NAME(session_name);
      setSESSION_STATUS(session_status);
      setIsOpenSessionDialog(false);
      fetchClassData(session_id);
    },
    [fetchClassData]
  );

  const goToPaperSettings = useCallback(() => {
    if (!createdExam) return;
    const { exam_id, session_id, exam_name, exam_status } = createdExam;
    history.push(
      `/AdminMain/ExamPaperSettings/${encodeURIComponent(
        exam_id
      )}/${encodeURIComponent(session_id)}/${encodeURIComponent(
        exam_name
      )}/${encodeURIComponent(exam_status)}`
    );
  }, [createdExam, history]);

  if (!loggedIn) {
    return <Redirect to="/Login" />;
  }

  if (loading) {
    return <MyLoader />;
  }

  return (
    <div
      className="content payroll-page sm-page sr-page ec-page"
      style={{ background: COLORS.white, fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Exam Creation</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaPlus />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Create Exam</h1>
              <p className="sm-hero__sub">
                Set exam details and select participating classes & subjects
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <button
              type="button"
              className="ec-session-pill"
              onClick={() => setIsOpenSessionDialog(true)}
              title="Change session"
            >
              Session: {SESSION_NAME || "Select"}
              {SESSION_STATUS ? ` (${SESSION_STATUS})` : ""}
            </button>
            <Link to={listBackTo} className="sr-hero-link">
              <FaArrowLeft aria-hidden /> {listBackLabel}
            </Link>
          </div>
        </header>

        <div className="sr-body">
          <div className="ec-kpi-row">
            <span className="ec-kpi">
              <FaClipboardList aria-hidden />
              {filteredClassSectionList.length} classes
            </span>
            <span className="ec-kpi">
              <FaUsers aria-hidden />
              {selectedClassTotal} selected
            </span>
            <span className="ec-kpi">
              <FaCheckCircle aria-hidden />
              {selectedSubjectTotal} subjects
            </span>
          </div>

          <SectionCard
            className="ec-details-section"
            icon={FaEdit}
            title="Exam Details"
            subtitle="Name, board, medium, and remark"
            action={
              <div className="ec-head-type" role="group" aria-label="Exam type">
                <span className="ec-head-type__label">
                  Exam Type <span className="sr-field__req">*</span>
                </span>
                <div className="ec-head-type__chips">
                  {EXAM_TYPES.map((type, idx) => (
                    <button
                      key={type.value}
                      type="button"
                      className={`ec-head-type__chip${
                        examType === type.value ? " is-active" : ""
                      }`}
                      onClick={() => setExamType(type.value)}
                    >
                      <span className="ec-head-type__num">{idx + 1}</span>
                      {type.value}
                    </button>
                  ))}
                </div>
              </div>
            }
          >
            <div className="ec-details">
              <div className="ec-details__name">
                <Field label="Exam Name" required>
                  <input
                    id="ExamName"
                    type="text"
                    className="sr-input ec-details__name-input"
                    placeholder="Enter exam name"
                    value={examName}
                    onFocus={() => setRemarkTouched(true)}
                    onChange={(e) => setExamName(e.target.value)}
                  />
                </Field>
              </div>

              <div className="ec-details__meta">
                <div className="sr-grid sr-grid--3">
                  <Field label="Board" required>
                    <select
                      className="sr-input sr-select"
                      onChange={(e) => setSelectedBoard(e.target.value)}
                      value={selectedBoard}
                    >
                      {classBoard.map((item) => (
                        <option key={item.board_id} value={item.board_id}>
                          {item.board_name}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Medium" required>
                    <select
                      className="sr-input sr-select"
                      onChange={(e) => setSelectedMedium(e.target.value)}
                      value={selectedMedium}
                    >
                      {classMedium.map((item) => (
                        <option key={item.medium_id} value={item.medium_id}>
                          {item.medium_name}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Remark">
                    <input
                      id="remark"
                      type="text"
                      className="sr-input"
                      placeholder="Enter remark (optional)"
                      value={remark}
                      onChange={(e) => {
                        setRemarkTouched(true);
                        setRemark(e.target.value);
                      }}
                    />
                  </Field>
                </div>
              </div>
            </div>
          </SectionCard>

          <SectionCard
            className="ec-classes-section"
            icon={FaUsers}
            title="Participating Classes & Subjects"
            subtitle="Select classes and subjects to include in this exam"
            action={
              <span className="ec-section-count">
                {filteredClassSectionList.length} classes
              </span>
            }
          >
            {filteredClassSectionList.length === 0 ? (
              <div className="ec-empty">
                <FaUniversity aria-hidden />
                <p className="ec-empty__title">No classes found</p>
                <p className="ec-empty__sub">
                  Try a different board or medium for this session.
                </p>
              </div>
            ) : (
              <div className="ec-class-list">
                {filteredClassSectionList.map((item) => {
                  const key = getItemKey(item);
                  const subjects = subjectsByKey[key] || [];
                  const selectedSubjectSet =
                    selectedSubjectsByClass[key] || null;
                  return (
                    <ClassCard
                      key={key}
                      item={item}
                      isActive={!!selectedClasses[key]}
                      selectedCount={selectedSubjectSet?.size || 0}
                      subjects={subjects}
                      selectedSubjectSet={selectedSubjectSet}
                      onToggleClass={toggleClassSelected}
                      onToggleSubject={toggleSubjectSelected}
                      onToggleAllSubjects={toggleAllSubjects}
                      onManageSubject={openManageSubject}
                    />
                  );
                })}
              </div>
            )}
          </SectionCard>

          <div className="sr-actions">
            <Link to={listBackTo} className="sr-btn sr-btn--ghost">
              <FaArrowLeft aria-hidden /> Cancel
            </Link>
            <button
              type="button"
              className="sr-btn sr-btn--primary"
              disabled={submitLoading}
              onClick={handleSubmitExamCreation}
            >
              <FaPlus aria-hidden />
              {submitLoading ? "Creating…" : "Create Exam"}
            </button>
          </div>
        </div>
      </div>

      {isOpenSessionDialog ? (
        <MdlSelectSession
          action={() => setIsOpenSessionDialog(false)}
          action2={handleSelectedSession}
        />
      ) : null}

      {isSuccessOpen ? (
        <div
          className="ec-modal-backdrop"
          onClick={() => setIsSuccessOpen(false)}
          role="presentation"
        >
          <div
            className="ec-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="ec-success-title"
          >
            <div className="ec-modal__head">
              <h3 id="ec-success-title">Exam created</h3>
              <button
                type="button"
                className="ec-modal__close"
                onClick={() => setIsSuccessOpen(false)}
              >
                Close
              </button>
            </div>
            <div className="ec-modal__body">
              <div className="ec-modal__icon" aria-hidden>
                <FaCheckCircle />
              </div>
              <p className="ec-modal__title">Exam created successfully</p>
              <p className="ec-modal__sub">
                Create another exam, open the list, or set papers now.
              </p>
              <div className="ec-modal__actions">
                <button
                  type="button"
                  className="sr-btn sr-btn--ghost"
                  onClick={() => setIsSuccessOpen(false)}
                >
                  Create Another
                </button>
                <Link to={listBackTo} className="sr-btn sr-btn--ghost">
                  Go to {listBackLabel}
                </Link>
                <button
                  type="button"
                  className="sr-btn sr-btn--primary"
                  disabled={!createdExam}
                  onClick={goToPaperSettings}
                >
                  <FaCog aria-hidden /> Set Exam Paper
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ExamCreation;
